import React, { useEffect, useState } from "react";
import { memberApi } from "@/lib/memberApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Clock, XCircle, CalendarClock, Lock, Info } from "lucide-react";
import { DateField } from "@/components/common/DateField";
import { formatDate, isoFromDisplay } from "@/lib/dateFormat";

const LOCKABLE = ["date_of_birth", "date_of_joining_service", "joining_advertisement_no", "joining_merit_no", "original_merit_no", "batch", "date_of_current_posting"];
const dur = (x) => (x ? `${x.years}y ${x.months}m ${x.days}d` : "—");

const Badge = ({ status }) => {
  if (status === "verified") return <span className="inline-flex items-center gap-1 rounded-full bg-gmoaa-green-light px-2 py-0.5 text-[10px] font-semibold text-gmoaa-green"><ShieldCheck size={11} /> Verified</span>;
  if (status === "rejected") return <span className="inline-flex items-center gap-1 rounded-full bg-gmoaa-red-bg px-2 py-0.5 text-[10px] font-semibold text-gmoaa-red"><XCircle size={11} /> Rejected</span>;
  if (status === "pending") return <span className="inline-flex items-center gap-1 rounded-full bg-gmoaa-saffron-light px-2 py-0.5 text-[10px] font-semibold text-gmoaa-saffron-hover"><Clock size={11} /> Pending verification</span>;
  return null;
};

export const ServiceDetails = () => {
  const [svc, setSvc] = useState(null);
  const [form, setForm] = useState({});
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = () => memberApi.getService().then((d) => {
    setSvc(d);
    setForm({
      date_of_birth: isoFromDisplay(d.date_of_birth_display) || "", date_of_joining_service: isoFromDisplay(d.date_of_joining_service_display) || "",
      joining_advertisement_no: d.joining_advertisement_no || "", joining_merit_no: d.joining_merit_no || "",
      original_merit_no: d.original_merit_no || "", batch: d.batch || "",
      date_of_current_posting: isoFromDisplay(d.date_of_current_posting_display) || "",
      educational_qualifications: d.educational_qualifications || [],
      deputation_in_college: d.deputation_in_college || [], posting_history: d.posting_history || [],
    });
    if (d.consent_at) setConsent(true);
  });
  useEffect(() => { refresh(); }, []);

  const vstatus = (f) => svc?.verification?.[f]?.status;
  const locked = (f) => vstatus(f) === "verified";
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setRow = (key, i, field) => (v) => setForm((f) => { const rows = [...f[key]]; rows[i] = { ...rows[i], [field]: v?.target ? v.target.value : v }; return { ...f, [key]: rows }; });
  const addRow = (key, blank) => () => setForm((f) => ({ ...f, [key]: [...(f[key] || []), blank] }));
  const delRow = (key, i) => () => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  const save = async () => {
    if (!consent) { toast.error("Please tick the consent box to save your service details"); return; }
    setSaving(true);
    const payload = {};
    for (const k of LOCKABLE) if (!locked(k)) payload[k] = form[k];
    payload.educational_qualifications = form.educational_qualifications;
    payload.deputation_in_college = form.deputation_in_college;
    payload.posting_history = form.posting_history;
    try { await memberApi.putService(payload); toast.success("Service details saved"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const requestCorrection = async () => {
    const note = window.prompt("What needs correcting? Admin will review.");
    if (!note) return;
    await memberApi.requestCorrection(note); toast.success("Correction request sent to admin");
  };

  if (!svc) return null;
  const c = svc.computed || {};
  const anyLocked = LOCKABLE.some(locked);
  const sfield = (label, key, type = "text", ph = "") => (
    <div>
      <Label className="mb-1 flex items-center gap-1 text-xs text-gmoaa-text-muted">{label} <Badge status={vstatus(key)} />{locked(key) && <Lock size={11} className="text-gmoaa-text-muted" />}</Label>
      {type === "date" ? (
        <DateField value={form[key] || ""} onChange={set(key)} disabled={locked(key)} testid={`svc-${key}`} disableFuture={key === "date_of_birth"} />
      ) : (
        <Input data-testid={`svc-${key}`} type={type} value={form[key] || ""} onChange={set(key)} placeholder={ph} disabled={locked(key)} className="border-gmoaa-border-subtle bg-white disabled:opacity-70" />
      )}
    </div>
  );

  return (
    <div data-testid="service-details" className="rounded-2xl border border-gmoaa-border-subtle bg-white p-5">
      <h3 className="mb-1 font-serif text-lg font-semibold text-gmoaa-green">My service details</h3>
      <p className="mb-4 flex items-start gap-1.5 text-xs text-gmoaa-text-muted"><Info size={13} className="mt-0.5 shrink-0" /> We collect these for seniority, retirement planning and service matters. Only you and the super admin can see your date of birth, merit numbers and postings — never shown publicly. You can request withdrawal anytime.</p>

      {c.retirement_date && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-xl bg-gmoaa-green-light/50 p-3 text-sm">
          <span className="flex items-center gap-1 font-semibold text-gmoaa-green" data-testid="svc-retirement"><CalendarClock size={15} /> Retires on {formatDate(c.retirement_date)} (age {c.retirement_age})</span>
          <span className="text-gmoaa-text-muted">Age: {c.age ?? "—"}</span>
          <span className="text-gmoaa-text-muted">Service: {dur(c.total_service)}</span>
          <span className="text-gmoaa-text-muted">At current place: {dur(c.duration_at_current_place)}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {sfield("Date of birth", "date_of_birth", "date")}
        {sfield("Date of joining service", "date_of_joining_service", "date")}
        {sfield("Joining advertisement no.", "joining_advertisement_no", "text", "e.g. 12/2014-15")}
        {sfield("Batch", "batch", "text", "e.g. 2014")}
        {sfield("Joining merit no.", "joining_merit_no", "number")}
        {sfield("Original merit no.", "original_merit_no", "number")}
        {sfield("Date of current posting", "date_of_current_posting", "date")}
      </div>

      {/* Educational qualifications */}
      <RepeatBlock title="Educational qualifications" rows={form.educational_qualifications || []} onAdd={addRow("educational_qualifications", { degree: "", institution: "", year: "", specialisation: "" })}
        cols={[["degree", "Degree"], ["institution", "College / University"], ["year", "Year"], ["specialisation", "Specialisation"]]} setRow={(i, f) => setRow("educational_qualifications", i, f)} delRow={(i) => delRow("educational_qualifications", i)} testid="qual" />
      {/* Deputation */}
      <RepeatBlock title="Deputation in college (optional)" rows={form.deputation_in_college || []} onAdd={addRow("deputation_in_college", { college: "", subject: "", from: "", to: "", order_no: "" })}
        cols={[["college", "College"], ["subject", "Subject / Dept"], ["from", "From (DD/MM/YYYY)"], ["to", "To (DD/MM/YYYY)"], ["order_no", "Order no."]]} setRow={(i, f) => setRow("deputation_in_college", i, f)} delRow={(i) => delRow("deputation_in_college", i)} testid="dep" />
      {/* Posting history */}
      <RepeatBlock title="Posting history (optional)" rows={form.posting_history || []} onAdd={addRow("posting_history", { place: "", from: "", to: "" })}
        cols={[["place", "Place"], ["from", "From (DD/MM/YYYY)"], ["to", "To (DD/MM/YYYY)"]]} setRow={(i, f) => setRow("posting_history", i, f)} delRow={(i) => delRow("posting_history", i)} testid="post" />

      <label className="mt-4 flex items-start gap-2 text-xs text-gmoaa-text-dark">
        <input type="checkbox" data-testid="svc-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
        I consent to GMOAA storing these service details (date of birth and merit numbers are encrypted) for seniority and retirement planning. I understand only I and the super admin can view them, and I can withdraw consent anytime.
      </label>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="svc-save" onClick={save} disabled={saving} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">{saving ? "Saving…" : "Save service details"}</Button>
        {anyLocked && <Button variant="outline" data-testid="svc-correction" onClick={requestCorrection} className="border-gmoaa-saffron text-gmoaa-saffron-hover">Request correction</Button>}
      </div>
      {anyLocked && <p className="mt-2 text-xs text-gmoaa-text-muted"><Lock size={11} className="mr-1 inline" /> Verified fields are locked. Use "Request correction" to ask the admin to change them.</p>}
    </div>
  );
};

const RepeatBlock = ({ title, rows, cols, onAdd, setRow, delRow, testid }) => (
  <div className="mt-4">
    <div className="mb-2 flex items-center justify-between">
      <p className="text-sm font-semibold text-gmoaa-text-dark">{title}</p>
      <Button size="sm" variant="outline" data-testid={`${testid}-add`} onClick={onAdd} className="border-gmoaa-border-subtle"><Plus size={13} className="mr-1" /> Add</Button>
    </div>
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2 rounded-lg border border-gmoaa-border-subtle p-2">
          {cols.map(([f, label]) => (
            <div key={f} className="min-w-[120px] flex-1">
              <Label className="mb-0.5 block text-[10px] text-gmoaa-text-muted">{label}</Label>
              {(f === "from" || f === "to") ? (
                <DateField value={row[f] || ""} onChange={setRow(i, f)} helper="" className="h-8 text-sm" />
              ) : (
                <Input value={row[f] || ""} onChange={setRow(i, f)} className="h-8 border-gmoaa-border-subtle bg-white text-sm" />
              )}
            </div>
          ))}
          <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={delRow(i)}><Trash2 size={14} /></Button>
        </div>
      ))}
      {rows.length === 0 && <p className="text-xs text-gmoaa-text-muted">None added.</p>}
    </div>
  </div>
);

export default ServiceDetails;
